import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Card, CardContent } from 'components/ui/card';
import { ScrollArea } from 'components/ui/scroll-area';
import { Badge } from 'components/ui/badge';
import { MapPinIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { toast } from 'sonner';
import { 
  getGeocodingErrors, 
  previewGeolocation, 
  saveGeolocation,
  type BadPincode,
  type GeolocationPreviewResponse,
  type GeolocationSaveRequest
} from '../api/shipments';

interface GeocodingErrorsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

interface EditingPincode {
  pincode: string;
  locationText: string;
  previewData: GeolocationPreviewResponse | null;
  isLoading: boolean;
  isSaving: boolean;
}

export const GeocodingErrorsModal: React.FC<GeocodingErrorsModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const [badPincodes, setBadPincodes] = useState<BadPincode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPincode, setEditingPincode] = useState<EditingPincode | null>(null);

  // Fetch geocoding errors when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchGeocodingErrors();
    }
  }, [isOpen]);

  const fetchGeocodingErrors = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getGeocodingErrors();
      setBadPincodes(response.bad_pincodes);
    } catch (err) {
      console.error('Error fetching geocoding errors:', err);
      setError('Failed to load geocoding errors');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPincode = (pincode: string) => {
    setEditingPincode({
      pincode,
      locationText: '',
      previewData: null,
      isLoading: false,
      isSaving: false
    });
  };

  const handleLocationTextChange = (text: string) => {
    if (editingPincode) {
      setEditingPincode({
        ...editingPincode,
        locationText: text,
        previewData: null // Clear preview when text changes
      });
    }
  };

  const handleFindLocation = async () => {
    if (!editingPincode || !editingPincode.locationText.trim()) {
      toast.error('Please enter a location to search');
      return;
    }

    setEditingPincode({
      ...editingPincode,
      isLoading: true
    });

    try {
      const response = await previewGeolocation({
        pincode: editingPincode.pincode,
        location_text: editingPincode.locationText.trim()
      });

      setEditingPincode({
        ...editingPincode,
        previewData: response,
        isLoading: false
      });

      if (!response.success) {
        toast.error(response.error_message || 'Location not found');
      }
    } catch (error) {
      console.error('Error previewing location:', error);
      setEditingPincode({
        ...editingPincode,
        isLoading: false
      });
      toast.error('Failed to find location');
    }
  };

  const handleSaveLocation = async () => {
    if (!editingPincode?.previewData || !editingPincode.previewData.success) {
      toast.error('No valid location to save');
      return;
    }

    // Set saving state
    setEditingPincode({
      ...editingPincode,
      isSaving: true
    });

    const previewData = editingPincode.previewData;
    
    // Prepare save request data
    const saveData: GeolocationSaveRequest = {
      pincode: previewData.pincode!,
      place_id: previewData.place_id!,
      lat: previewData.lat!,
      lon: previewData.lon!,
      formatted_address: previewData.formatted_address!,
      sublocality: previewData.sublocality,
      locality: previewData.locality,
      admin_area_level_3: previewData.admin_area_level_3,
      admin_area_level_2: previewData.admin_area_level_2,
      admin_area_level_1: previewData.admin_area_level_1,
      admin_area1_code: previewData.admin_area1_code,
      country: previewData.country!,
      country_code: previewData.country_code!
    };

    try {
      const response = await saveGeolocation(saveData);
      
      if (response.success) {
        toast.success(response.message, {
          style: {
            background: '#f0fdf4',
            color: '#15803d',
            border: '1px solid #bbf7d0'
          }
        });
        
        // Remove the saved pincode from the list
        setBadPincodes(prev => prev.filter(p => p.pincode !== editingPincode.pincode));
        setEditingPincode(null);
        onSuccess?.();
      } else {
        toast.error('Failed to save location', {
          style: {
            background: '#fef2f2',
            color: '#dc2626',
            border: '1px solid #fecaca'
          }
        });
      }
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error('Failed to save location', {
        style: {
          background: '#fef2f2',
          color: '#dc2626',
          border: '1px solid #fecaca'
        }
      });
    } finally {
      // Clear saving state
      if (editingPincode) {
        setEditingPincode({
          ...editingPincode,
          isSaving: false
        });
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingPincode(null);
  };

  const handleClose = () => {
    setEditingPincode(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] !max-w-none max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg font-semibold">
            <MapPinIcon className="h-5 w-5" />
            Geocoding Errors
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-base font-medium text-red-600 mb-2">Error</div>
                <div className="text-sm text-gray-600">{error}</div>
                <Button 
                  onClick={fetchGeocodingErrors}
                  className="mt-4"
                  variant="outline"
                  size="sm"
                >
                  Retry
                </Button>
              </div>
            </div>
          ) : badPincodes.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <MapPinIcon className="h-10 w-10 text-gray-400 mx-auto mb-3" />
                <div className="text-base font-medium text-gray-600 mb-2">No Geocoding Errors</div>
                <div className="text-sm text-gray-500">All pincodes have been successfully geocoded</div>
              </div>
            </div>
          ) : (
            <ScrollArea className="h-full pr-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4 p-1">
                {badPincodes.map((badPincode) => (
                  <Card key={badPincode.pincode} className="border border-gray-200 hover:border-gray-300 transition-colors">
                    <CardContent className="p-3">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <Badge variant="outline" className="font-mono text-xs">
                              {badPincode.pincode}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {badPincode.addresses.length} address{badPincode.addresses.length !== 1 ? 'es' : ''}
                            </span>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            {badPincode.addresses.map((address, index) => (
                              <Badge key={index} variant="secondary" className="text-xs px-2 py-0.5">
                                {address}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <Button
                          onClick={() => handleEditPincode(badPincode.pincode)}
                          size="sm"
                          className="ml-2 text-xs px-3 py-1 h-7"
                          disabled={editingPincode?.pincode === badPincode.pincode}
                        >
                          Fix Location
                        </Button>
                      </div>

                    {/* Editing interface */}
                    {editingPincode?.pincode === badPincode.pincode && (
                      <div className="border-t pt-4 mt-4">
                        <div className="space-y-4">
                          <div>
                            <label className="text-xs font-medium text-gray-700 mb-2 block">
                              Enter location details for pincode {badPincode.pincode}
                            </label>
                            <div className="space-y-2">
                              <Input
                                placeholder="e.g., Gateway of India, Apollo Bandar, Mumbai"
                                value={editingPincode.locationText}
                                onChange={(e) => handleLocationTextChange(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    handleFindLocation();
                                  }
                                }}
                                disabled={editingPincode.isLoading}
                                className="text-xs h-8 w-full"
                              />
                              <Button
                                onClick={handleFindLocation}
                                disabled={editingPincode.isLoading || !editingPincode.locationText.trim()}
                                size="sm"
                                className="text-xs px-3 py-1.5 h-8 w-full"
                              >
                                {editingPincode.isLoading ? (
                                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                                ) : (
                                  <>
                                    <MagnifyingGlassIcon className="h-3 w-3 mr-1" />
                                    Find Location
                                  </>
                                )}
                              </Button>
                            </div>
                          </div>

                            {/* Preview results */}
                            {editingPincode.previewData && (
                              <div className="bg-gray-50 rounded-md p-3">
                                {editingPincode.previewData.success ? (
                                  <div>
                                    <div className="text-xs font-medium text-green-700 mb-2">
                                      Location Found
                                    </div>
                                    <div className="text-xs text-gray-700 mb-2 leading-relaxed">
                                      {editingPincode.previewData.formatted_address}
                                    </div>
                                    <div className="text-xs text-gray-500 mb-3">
                                      Coordinates: {editingPincode.previewData.lat}, {editingPincode.previewData.lon}
                                    </div>
                                     <div className="flex gap-2">
                                       <Button
                                         onClick={handleSaveLocation}
                                         size="sm"
                                         className="bg-green-600 hover:bg-green-700 text-xs px-3 py-1 h-7"
                                         disabled={editingPincode.isSaving}
                                       >
                                         {editingPincode.isSaving ? (
                                           <>
                                             <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white mr-1"></div>
                                             Saving...
                                           </>
                                         ) : (
                                           'Save Location'
                                         )}
                                       </Button>
                                       <Button
                                         onClick={handleCancelEdit}
                                         size="sm"
                                         variant="outline"
                                         className="text-xs px-3 py-1 h-7"
                                         disabled={editingPincode.isSaving}
                                       >
                                         Cancel
                                       </Button>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <div className="text-xs font-medium text-red-700 mb-2">
                                      Location Not Found
                                    </div>
                                    <div className="text-xs text-gray-600 mb-3">
                                      {editingPincode.previewData.error_message || 'No match found for the given location'}
                                    </div>
                                    <Button
                                      onClick={handleCancelEdit}
                                      size="sm"
                                      variant="outline"
                                      className="text-xs px-3 py-1 h-7"
                                    >
                                      Try Again
                                    </Button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>

        <div className="flex justify-end pt-3 border-t">
          <Button onClick={handleClose} variant="outline" size="sm" className="text-xs px-4 py-2">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
